import { css, cssManager } from '@design.estate/dees-element';
import { DeesInputBase } from '../dees-input-base/dees-input-base.js';
import { themeDefaultStyles } from '../../00theme.js';

export const richtextStyles = [
    themeDefaultStyles,
    ...DeesInputBase.baseStyles,
    cssManager.defaultStyles,
    css`
      :host {
        display: block;
        position: relative;
        font-family: Inter, system-ui, 'Segoe UI', Roboto, sans-serif;
      }

      .input-wrapper {
        position: relative;
      }

      .label {
        display: block;
        margin-bottom: 8px;
        font-size: 14px;
        font-weight: 500;
        color: var(--dees-color-text-primary);
      }

      dees-tile {
        min-height: 200px;
      }

      dees-tile:hover::part(outer) {
        border-color: var(--dees-color-border-strong);
      }

      dees-tile.focused::part(outer) {
        border-color: var(--dees-color-tooltip-bg);
        box-shadow: 0 0 0 2px color-mix(in srgb, var(--dees-color-text-primary) 5%, transparent);
      }

      .editor-toolbar {
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
        padding: 4px 12px;
        align-items: center;
        position: relative;
      }

      .toolbar-button {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        border: none;
        border-radius: 4px;
        background: transparent;
        cursor: pointer;
        font-size: 14px;
        font-weight: 500;
        color: var(--dees-color-text-muted);
        transition: all 0.15s ease;
        user-select: none;
      }

      .toolbar-button dees-icon {
        width: 16px;
        height: 16px;
      }

      .toolbar-button:hover {
        background: var(--dees-color-hover);
        color: var(--dees-color-text-primary);
      }

      .toolbar-button.active {
        background: var(--dees-color-tooltip-bg);
        color: var(--dees-color-bg-primary);
      }

      .toolbar-button:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .toolbar-divider {
        width: 1px;
        height: 24px;
        background: var(--dees-color-border-default);
        margin: 0 4px;
      }

      .editor-content {
        position: absolute;
        inset: 0;
        padding: 16px;
        overflow-y: auto;
      }

      .editor-content .ProseMirror {
        outline: none;
        line-height: 1.6;
        color: var(--dees-color-text-primary);
        min-height: 100%;
        white-space: pre-wrap;
        word-wrap: break-word;
      }

      .editor-content .ProseMirror p {
        margin: 0.5em 0;
      }

      .editor-content .ProseMirror p:first-child {
        margin-top: 0;
      }

      .editor-content .ProseMirror p:last-child {
        margin-bottom: 0;
      }

      .editor-content .ProseMirror h1 {
        font-size: 2em;
        font-weight: bold;
        margin: 1em 0 0.5em 0;
        line-height: 1.2;
      }

      .editor-content .ProseMirror h2 {
        font-size: 1.5em;
        font-weight: bold;
        margin: 1em 0 0.5em 0;
        line-height: 1.3;
      }

      .editor-content .ProseMirror h3 {
        font-size: 1.25em;
        font-weight: bold;
        margin: 1em 0 0.5em 0;
        line-height: 1.4;
      }

      .editor-content .ProseMirror ul,
      .editor-content .ProseMirror ol {
        padding-left: 1.5em;
        margin: 0.5em 0;
      }

      .editor-content .ProseMirror li {
        margin: 0.25em 0;
      }

      .editor-content .ProseMirror blockquote {
        border-left: 4px solid var(--dees-color-border-default);
        margin: 1em 0;
        padding-left: 1em;
        color: var(--dees-color-text-muted);
        font-style: italic;
      }

      .editor-content .ProseMirror code {
        background: var(--dees-color-bg-tertiary);
        border-radius: 3px;
        padding: 0.2em 0.4em;
        font-family: 'Intel One Mono', 'Fira Code', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace;
        font-size: 0.9em;
        color: var(--dees-color-text-primary);
      }

      .editor-content .ProseMirror pre {
        background: var(--dees-color-text-primary);
        color: var(--dees-color-bg-secondary);
        border-radius: 6px;
        padding: 1em;
        margin: 1em 0;
        overflow-x: auto;
      }

      .editor-content .ProseMirror pre code {
        background: none;
        color: inherit;
        padding: 0;
        border-radius: 0;
      }

      .editor-content .ProseMirror a {
        color: var(--dees-color-accent-primary);
        text-decoration: underline;
        cursor: pointer;
      }

      .editor-content .ProseMirror a:hover {
        color: color-mix(in srgb, var(--dees-color-link) 80%, var(--dees-color-text-primary));
      }

      .editor-footer {
        padding: 0 12px;
        height: 28px;
        font-size: 11px;
        color: var(--dees-color-text-muted);
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        box-sizing: border-box;
      }

      .word-count {
        font-weight: 500;
      }

      .link-input {
        display: none;
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        background: var(--dees-color-bg-primary);
        border: 1px solid var(--dees-color-border-default);
        border-radius: 6px;
        box-shadow: var(--dees-shadow-md);
        padding: 12px;
        z-index: 1000;
      }

      .link-input.show {
        display: block;
      }

      .link-input input {
        width: 100%;
        padding: 8px 12px;
        border: 1px solid var(--dees-color-border-default);
        border-radius: 6px;
        outline: none;
        font-size: 14px;
        background: var(--dees-color-bg-primary);
        color: var(--dees-color-text-primary);
        transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
      }

      .link-input input:focus {
        border-color: var(--dees-color-tooltip-bg);
        box-shadow: 0 0 0 2px color-mix(in srgb, var(--dees-color-text-primary) 5%, transparent);
      }

      .link-input-buttons {
        display: flex;
        gap: 8px;
        margin-top: 8px;
      }

      .link-input-buttons button {
        padding: 6px 12px;
        border: 1px solid var(--dees-color-border-default);
        border-radius: 4px;
        background: var(--dees-color-bg-primary);
        cursor: pointer;
        font-size: 12px;
        color: var(--dees-color-text-muted);
        transition: all 0.15s ease;
        font-weight: 500;
      }

      .link-input-buttons button:hover {
        background: var(--dees-color-hover);
        color: var(--dees-color-text-primary);
      }

      .link-input-buttons button.primary {
        background: var(--dees-color-tooltip-bg);
        color: var(--dees-color-bg-primary);
        border-color: var(--dees-color-tooltip-bg);
      }

      .link-input-buttons button.primary:hover {
        background: var(--dees-color-tooltip-bg);
        border-color: var(--dees-color-tooltip-bg);
      }

      :host([disabled]) dees-tile {
        opacity: 0.6;
        cursor: not-allowed;
      }

      :host([disabled]) .toolbar-button,
      :host([disabled]) .editor-content {
        pointer-events: none;
      }
    `,
  ];

